import type { MaybeRefOrGetter } from 'vue';

import { computed, toValue } from 'vue';

import type { Temporal } from '@shared/lib/date-time';

import {
  clock,
  parsePlainDate,
  formatDate,
  formatDateTime,
  formatRelativeDateTime,
  formatTime,
  formatRelativeDate,
  formatLongDate,
  formatDayMonth,
  inTimezone,
  todayInTimezone,
} from '@shared/lib/date-time';

import { useAuthStore } from '../model/auth-store';

export function useAuthTime() {
  const auth = useAuthStore();
  return {
    locationTime: (value: string | Temporal.Instant = clock.now()) => inTimezone(value, auth.currentLocation?.timezone),
    organizationTime: (value: string | Temporal.Instant = clock.now()) =>
      inTimezone(value, auth.currentOrganization?.timezone),
    locationToday: () => todayInTimezone(auth.currentLocation?.timezone),
    organizationToday: () => todayInTimezone(auth.currentOrganization?.timezone),
  };
}

export function useOrganizationToday() {
  const { organizationToday } = useAuthTime();
  return computed(() => organizationToday());
}

/** Форматирование дат в явной зоне: записи другой локации и строки журналов по локациям. */
export function useZoneDateFormatters(zone: MaybeRefOrGetter<string | null | undefined>) {
  const inZone = (value: string | null | undefined) => (value ? inTimezone(value, toValue(zone)) : null);
  const calendar = (value: string | null | undefined) => parsePlainDate(value) ?? inZone(value);
  return {
    formatDate: (value: string | null | undefined) => formatDate(calendar(value)),
    formatDateTime: (value: string | null | undefined) => formatDateTime(inZone(value)),
    formatRelativeDateTime: (value: string | null | undefined) => formatRelativeDateTime(inZone(value)),
    formatTime: (value: string | null | undefined) => formatTime(inZone(value)),
    formatRelativeDate: (value: string | null | undefined) => formatRelativeDate(inZone(value)),
    formatLongDate: (value: string | null | undefined) => formatLongDate(calendar(value)),
    formatDayMonth: (value: string | null | undefined) => formatDayMonth(calendar(value)),
  };
}

/** Форматирование дат текущего рабочего или сетевого контекста. */
export function useAuthDateFormatters(scope: 'location' | 'organization') {
  const auth = useAuthStore();

  return useZoneDateFormatters(() =>
    scope === 'location' ? auth.currentLocation?.timezone : auth.currentOrganization?.timezone,
  );
}
